/**
 * App Layout Component
 * 
 * Shared layout for all authenticated pages.
 * Renders a collapsible left sidebar for admin users with navigation to:
 *   - Dashboards, Users, Embed Settings
 * Non-admin (viewer) users see full-width content with no sidebar.
 */

import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Tune as TuneIcon,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useSudo } from '../hooks/useSudo';
import { Header } from './Header';
import { SudoBanner } from './SudoBanner';

const SIDEBAR_WIDTH_OPEN = 220;
const SIDEBAR_WIDTH_COLLAPSED = 60;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const adminNavItems: NavItem[] = [
  { label: 'Dashboards', path: '/', icon: <DashboardIcon /> },
  { label: 'Users', path: '/users', icon: <PeopleIcon /> },
  { label: 'Embed Settings', path: '/embed-settings', icon: <TuneIcon /> },
];

export function AppLayout() {
  const { isAdmin } = useAuth();
  const { isSudoMode } = useSudo();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/viewer');
    }
    return location.pathname.startsWith(path);
  };

  // Viewers (and admins in sudo mode): full-width, no sidebar — identical viewer experience
  if (!isAdmin || isSudoMode) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
        <Header />
        <SudoBanner />
        <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    );
  }

  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED;

  // Admins: header + collapsible sidebar + content
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.2s ease-in-out',
            overflow: 'hidden',
          }}
        >
          {/* Toggle button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: sidebarOpen ? 'flex-end' : 'center',
              px: sidebarOpen ? 1 : 0,
              pt: 1,
              pb: 0.5,
            }}
          >
            <IconButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {sidebarOpen ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
            </IconButton>
          </Box>

          <List component="div" sx={{ px: sidebarOpen ? 1 : 0.5, py: 0 }}>
            {adminNavItems.map((item) => {
              const isActive = isActiveRoute(item.path);

              const button = (
                <ListItemButton
                  key={item.path}
                  component={Link}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    py: 1,
                    px: sidebarOpen ? 1.5 : 0,
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    minHeight: 40,
                    '&.Mui-selected': {
                      bgcolor: 'grey.100',
                      '&:hover': { bgcolor: 'grey.200' },
                    },
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarOpen ? 34 : 0,
                      mr: sidebarOpen ? undefined : 0,
                      justifyContent: 'center',
                      color: isActive ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {sidebarOpen && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? 'text.primary' : 'text.secondary',
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              );

              // Show tooltip with label when sidebar is collapsed
              if (!sidebarOpen) {
                return (
                  <Tooltip key={item.path} title={item.label} placement="right" arrow>
                    {button}
                  </Tooltip>
                );
              }

              return button;
            })}
          </List>
        </Box>

        {/* Content Area */}
        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: 'grey.50',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default AppLayout;
